import { useEffect, useRef } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import './DashboardTour.css';

const DashboardTour = ({ run, onComplete }) => {
  const tourRef = useRef(null);

  useEffect(() => {
    // Check if mobile - skip tour on mobile
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    // Only run if the run prop is true
    if (!run) {
      return;
    }

    // Create custom overlay div
    const customOverlay = document.createElement('div');
    customOverlay.id = 'custom-tour-overlay';
    customOverlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9998;
      pointer-events: none;
    `;
    document.body.appendChild(customOverlay);

    // Create the tour WITHOUT Shepherd's default overlay
    const tour = new Shepherd.Tour({
      useModalOverlay: false, // Disable Shepherd's overlay, use our custom one
      defaultStepOptions: {
        cancelIcon: {
          enabled: true,
          label: '' // Remove tooltip text
        },
        scrollTo: { behavior: 'smooth', block: 'center' }
      }
    });

    // Store tour reference
    tourRef.current = tour;

    // Step 1: Welcome (centered modal, no target)
    tour.addStep({
      id: 'welcome',
      title: 'Welcome!',
      text: '<p class="shepherd-step-counter">Step 1 of 5</p><p>Welcome to your dashboard. Here\'s a quick look around.</p>',
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'shepherd-button-skip',
          action() {
            tour.cancel();
          }
        },
        {
          text: 'Next',
          classes: 'shepherd-button-primary',
          action() {
            tour.next();
          }
        }
      ],
      modalOverlayOpeningPadding: 0
    });

    // Step 2: Schedule & Messages
    tour.addStep({
      id: 'schedule-messages',
      title: 'Your Schedule & Messages',
      text: '<p class="shepherd-step-counter">Step 2 of 5</p><p>Your schedule and messages. Upcoming sessions and client communications live here.</p>',
      attachTo: {
        element: '[data-tour="schedule-messages"]',
        on: 'bottom'
      },
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'shepherd-button-skip',
          action() {
            tour.cancel();
          }
        },
        {
          text: 'Back',
          classes: 'shepherd-button-back',
          action() {
            tour.back();
          }
        },
        {
          text: 'Next',
          classes: 'shepherd-button-primary',
          action() {
            tour.next();
          }
        }
      ],
      modalOverlayOpeningPadding: 4
    });

    // Step 3: Site Editor
    tour.addStep({
      id: 'site-editor',
      title: 'Site Editor',
      text: '<p class="shepherd-step-counter">Step 3 of 5</p><p>Your Site Editor. Build and customize your website, then publish when you\'re ready.</p>',
      attachTo: {
        element: '[data-tour="site-editor"]',
        on: 'top'
      },
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'shepherd-button-skip',
          action() {
            tour.cancel();
          }
        },
        {
          text: 'Back',
          classes: 'shepherd-button-back',
          action() {
            tour.back();
          }
        },
        {
          text: 'Next',
          classes: 'shepherd-button-primary',
          action() {
            tour.next();
          }
        }
      ],
      modalOverlayOpeningPadding: 4
    });

    // Step 4: Business Playbook
    tour.addStep({
      id: 'business-playbook',
      title: 'Business Playbook',
      text: '<p class="shepherd-step-counter">Step 4 of 5</p><p>Your Business Playbook. Guides and resources to help you grow your practice.</p>',
      attachTo: {
        element: '[data-tour="business-playbook"]',
        on: 'top'
      },
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'shepherd-button-skip',
          action() {
            tour.cancel();
          }
        },
        {
          text: 'Back',
          classes: 'shepherd-button-back',
          action() {
            tour.back();
          }
        },
        {
          text: 'Next',
          classes: 'shepherd-button-primary',
          action() {
            tour.next();
          }
        }
      ],
      modalOverlayOpeningPadding: 4
    });

    // Step 5: Account Menu
    tour.addStep({
      id: 'settings',
      title: 'Your Account Menu',
      text: '<p class="shepherd-step-counter">Step 5 of 5</p><p>Your account menu. Access settings, availability, payments, and account preferences from here.</p>',
      attachTo: {
        element: '[data-tour="settings"]',
        on: 'bottom'
      },
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'shepherd-button-skip',
          action() {
            tour.cancel();
          }
        },
        {
          text: 'Back',
          classes: 'shepherd-button-back',
          action() {
            tour.back();
          }
        },
        {
          text: 'Done',
          classes: 'shepherd-button-primary',
          action() {
            tour.complete();
          }
        }
      ],
      modalOverlayOpeningPadding: 4
    });

    // Handle tour completion
    tour.on('complete', () => {
      // Remove custom overlay
      const overlay = document.getElementById('custom-tour-overlay');
      if (overlay) {
        overlay.remove();
      }
      if (onComplete) {
        onComplete();
      }
    });

    // Handle tour cancellation
    tour.on('cancel', () => {
      // Remove custom overlay
      const overlay = document.getElementById('custom-tour-overlay');
      if (overlay) {
        overlay.remove();
      }
      if (onComplete) {
        onComplete();
      }
    });

    // Start the tour
    tour.start();

    // Remove title attributes from cancel icons to prevent browser tooltips
    // Use setTimeout to ensure elements are rendered
    setTimeout(() => {
      document.querySelectorAll('.shepherd-cancel-icon').forEach(el => {
        el.removeAttribute('title');
        el.parentElement?.removeAttribute('title');
        el.setAttribute('aria-label', 'Close tour');
      });
    }, 100);

    // Also remove title attributes when step changes
    tour.on('show', () => {
      setTimeout(() => {
        document.querySelectorAll('.shepherd-cancel-icon').forEach(el => {
          el.removeAttribute('title');
          el.parentElement?.removeAttribute('title');
          el.setAttribute('aria-label', 'Close tour');
        });
      }, 50);
    });

    // Cleanup function
    return () => {
      if (tourRef.current && tourRef.current.isActive()) {
        tourRef.current.complete();
      }
      // Remove custom overlay on cleanup
      const overlay = document.getElementById('custom-tour-overlay');
      if (overlay) {
        overlay.remove();
      }
    };
  }, [run, onComplete]);

  return null;
};

export default DashboardTour;
